export {};
import { AlarmLevel } from './alarm-level';
import { ChannelType, ChannelTypeStrings } from './channel-type';
import { systemChannelType } from './system-channels';

/**
 * @property {number} channelId
 * @property {string} vesselId
 * @property {number} TextId
 * @property {number} fcuNo
 * @property {string} channelGroupId
 * @property {Object} measurableRange
 * @property {number} measurableRange.max
 * @property {number} measurableRange.min
 * @property {Object} normalRange
 * @property {number} normalRange.max
 * @property {number} normalRange.min
 * @property {number} FixedDigit
 * @property {number} scale
 * @property {Object} inputData
 * @property {Object} inputData.fuInputAddress
 * @property {number} inputData.fuInputAddress.fuNo
 * @property {number} inputData.fuInputAddress.slotNo
 * @property {number} inputData.fuInputAddress.pin
 * @property {number} inputData.fuInputAddress.pinCount
 * @property {Object} inputData.memoryInputAddress
 * @property {number} inputData.memoryInputAddress.address
 * @property {number} inputData.memoryInputAddress.mask
 * @property {Object} inputData.misc
 * @property {number} inputData.misc.digitalInputFilter
 * @property {number} inputData.misc.AlarmRecoveryHysteresis
 * @property {Object} outputData
 * @property {Object} outputData.memoryOutputAddress
 * @property {number} outputData.memoryOutputAddress.address
 * @property {number} outputData.memoryOutputAddress.mask
 * @property {Object} outputData.misc
 * @property {number} outputData.misc.setPoint
 * @property {boolean} outputData.misc.signalType
 * @property {number} outputData.misc.pulseWidth
 * @property {number} outputData.misc.hysteresisForOpen
 * @property {number} outputData.misc.hysteresisForClose
 * @property {Object} digitalAlarmSetting
 * @property {Object} motorChannelSetting
 * @property {Setting} alarmSettingVeryHigh
 * @property {Setting} alarmSettingHigh
 * @property {Setting} alarmSettingLow
 * @property {Setting} alarmSettingVeryLow
 * @property {Setting} alarmSettingSensorFail
 * @property {Setting} alarmSettingFeedbackError
 * @property {Setting} systemAlarmSetting
 * @property {number} fixedDigit
 * @property {number} digitalInputFilter
 * @property {string} standardNameId
 */

/**
 *  @typedef {object} ChannelOpsData
 *  @property {number} id
 *  @property {string} name
 *  @property {string} tag //channelNo
 *  @property {string} vesselId
 */

/**
 *  @typedef {object} BlobFileMetadata
 *  @property {string} md5
 */

/**
 * @typedef  {Array<Object>} ExcelSheetData
 * @property {string} sheetName - The name of the sheet.
 * @property {Array<Array<string>>} datas - The data of the sheet in a array format.
 */

const ExcelHeaderRowCount = 4;
const ChannelRowKey = 'rowId';

const ExcelGroupDisplayNameDataRowCount = 4;

const ExcelSkipParsingSheets = new Set(['comboboxes', 'groups', 'Group']);

const ExcelGroupCategory = 'Group';

const ExcelParsedProperties = {
    Id: 'id',
    FcuNo: 'fcu no.',
    ChannelType: 'channel type',
    ChannelTypeSimple: 'simple channel type',
    Tag: 'ch. no. (tag)',
    Name: 'name',
    Status: 'status',
    MeasurableRangeMin: 'measurable range.min',
    MeasurableRangeMax: 'measurable range.max',
    NormalRangeMin: 'normal range.min',
    NormalRangeMax: 'normal range.max',
    MeasurementUnit: 'unit',
    Scale: 'scale',
    FixedDigit: 'fixed digit',

    InputDataFuInputAddressFuNo: 'input.fu.no.',
    InputDataFuInputAddressSlotNo: 'input.fu.slot',
    InputDataFuInputAddressPin: 'input.fu.pin',
    InputDataFuInputAddressPinCount: 'input.fu.pin count',
    InputDataMemoryInputAddressAddress: 'input.memory input.address',
    InputDataMemoryInputAddressMask: 'input.memory input.mask',
    InputDataMiscDigitalInputFilter: 'input.misc.digital input filter',
    InputDataMiscHysteresis: 'input.misc.alarm recovery hysteresis (%)',

    OutputDataFuOutputAddressFuNo: 'output.fu.no.',
    OutputDataFuOutputAddressSlotNo: 'output.fu.slot',
    OutputDataFuOutputAddressPin: 'output.fu.pin',
    OutputDataFuOutputAddressPinCount: 'output.fu.pin count',
    OutputDataMemoryOutputAddressAddress: 'output.memory output.address',
    OutputDataMemoryOutputAddressMask: 'output.memory output.mask',
    OutputDataMiscSetPoint: 'output.misc.set point',
    OutputDataMiscSignalType: 'output.misc.output signal type',
    OutputDataMiscSignalWidth: 'output.misc.signal width (msec)',
    OutputDataMiscHysteresisForOpen: 'output.misc.hysteresis for open (%)',
    OutputDataMiscHysteresisForClose: 'output.misc.hysteresis for close (%)',

    DigitalAlarmSettingEnable: 'digital alarm settings.enable',
    DigitalAlarmSettingStatusStringContactOn: 'digital alarm settings.on status',
    DigitalAlarmSettingStatusStringContactOff: 'digital alarm settings.off status',
    DigitalAlarmSettingAlarmGroupNo: 'digital alarm settings.ex',
    DigitalAlarmSettingReposeGroup1No: 'digital alarm settings.r1',
    DigitalAlarmSettingReposeGroup2No: 'digital alarm settings.r2',
    DigitalAlarmSettingDelayTimer: 'digital alarm settings.delay (sec)',
    DigitalAlarmSettingNormalOpenFlag: 'digital alarm settings.no',
    DigitalAlarmSettingAlarmLevel: 'digital alarm settings.alarm',

    MotorChannelSettingEnable: 'alarm settings.motor channel settings.enable',
    MotorChannelSettingMotorType: 'alarm settings.motor channel settings.motor type',
    MotorChannelSettingStopStatusString: 'alarm settings.motor channel settings.stop status',
    MotorChannelSettingStbyStatusString: 'alarm settings.motor channel settings.stby status',
    MotorChannelSettingRunStatusString1: 'alarm settings.motor channel settings.run status 1',
    MotorChannelSettingRunStatusString2: 'alarm settings.motor channel settings.run status 2',
    MotorChannelSettingRunStatusString3: 'alarm settings.motor channel settings.run status 3',
    MotorChannelSettingRunStatusString4: 'alarm settings.motor channel settings.run status 4',
    MotorChannelSettingRunStatusString5: 'alarm settings.motor channel settings.run status 5',
    MotorChannelSettingAlarmGroupNo: 'alarm settings.motor channel settings.ex',
    MotorChannelSettingReposeGroup1No: 'alarm settings.motor channel settings.r1',
    MotorChannelSettingReposeGroup2No: 'alarm settings.motor channel settings.r2',
    MotorChannelSettingDelayTimer: 'alarm settings.motor channel settings.delay (sec)',
    MotorChannelSettingAlarmLevel: 'alarm settings.motor channel settings.alarm',

    AlarmSettingVeryHighEnable: 'alarm settings.high-high.enable',
    AlarmSettingVeryHighStatusString: 'alarm settings.high-high.status',
    AlarmSettingVeryHighSetPoint: 'alarm settings.high-high.set point',
    AlarmSettingVeryHighAlarmGroupNo: 'alarm settings.high-high.ex',
    AlarmSettingVeryHighReposeGroup1No: 'alarm settings.high-high.r1',
    AlarmSettingVeryHighReposeGroup2No: 'alarm settings.high-high.r2',
    AlarmSettingVeryHighDelayTimer: 'alarm settings.high-high.delay (sec)',
    AlarmSettingVeryHighAlarmLevel: 'alarm settings.high-high.alarm',

    AlarmSettingHighEnable: 'alarm settings.high.enable',
    AlarmSettingHighStatusString: 'alarm settings.high.status',
    AlarmSettingHighSetPoint: 'alarm settings.high.set point',
    AlarmSettingHighAlarmGroupNo: 'alarm settings.high.ex',
    AlarmSettingHighReposeGroup1No: 'alarm settings.high.r1',
    AlarmSettingHighReposeGroup2No: 'alarm settings.high.r2',
    AlarmSettingHighDelayTimer: 'alarm settings.high.delay (sec)',
    AlarmSettingHighAlarmLevel: 'alarm settings.high.alarm',

    AlarmSettingLowEnable: 'alarm settings.low.enable',
    AlarmSettingLowStatusString: 'alarm settings.low.status',
    AlarmSettingLowSetPoint: 'alarm settings.low.set point',
    AlarmSettingLowAlarmGroupNo: 'alarm settings.low.ex',
    AlarmSettingLowReposeGroup1No: 'alarm settings.low.r1',
    AlarmSettingLowReposeGroup2No: 'alarm settings.low.r2',
    AlarmSettingLowDelayTimer: 'alarm settings.low.delay (sec)',
    AlarmSettingLowAlarmLevel: 'alarm settings.low.alarm',

    AlarmSettingVeryLowEnable: 'alarm settings.low-low.enable',
    AlarmSettingVeryLowStatusString: 'alarm settings.low-low.status',
    AlarmSettingVeryLowSetPoint: 'alarm settings.low-low.set point',
    AlarmSettingVeryLowAlarmGroupNo: 'alarm settings.low-low.ex',
    AlarmSettingVeryLowReposeGroup1No: 'alarm settings.low-low.r1',
    AlarmSettingVeryLowReposeGroup2No: 'alarm settings.low-low.r2',
    AlarmSettingVeryLowDelayTimer: 'alarm settings.low-low.delay (sec)',
    AlarmSettingVeryLowAlarmLevel: 'alarm settings.low-low.alarm',

    AlarmSettingSensorFailEnable: 'alarm settings.sensor failure.enable',
    AlarmSettingSensorFailStatusString: 'alarm settings.sensor failure.status',
    AlarmSettingSensorFailAlarmGroupNo: 'alarm settings.sensor failure.ex',
    AlarmSettingSensorFailReposeGroup1No: 'alarm settings.sensor failure.r1',
    AlarmSettingSensorFailReposeGroup2No: 'alarm settings.sensor failure.r2',
    AlarmSettingSensorFailDelayTimer: 'alarm settings.sensor failure.delay (sec)',
    AlarmSettingSensorFailAlarmLevel: 'alarm settings.sensor failure.alarm',

    AlarmSettingFeedbackErrorEnable: 'alarm settings.feedback.enable',
    AlarmSettingFeedbackErrorStatusString: 'alarm settings.feedback.status',
    AlarmSettingFeedbackErrorSetPoint: 'alarm settings.feedback.set point',
    AlarmSettingFeedbackErrorAlarmGroupNo: 'alarm settings.feedback.ex',
    AlarmSettingFeedbackErrorReposeGroup1No: 'alarm settings.feedback.r1',
    AlarmSettingFeedbackErrorReposeGroup2No: 'alarm settings.feedback.r2',
    AlarmSettingFeedbackErrorDelayTimer: 'alarm settings.feedback.delay (sec)',
    AlarmSettingFeedbackErrorAlarmLevel: 'alarm settings.feedback.alarm',

    SystemAlarmSettingEnable: 'alarm settings.system alarm.enable',
    SystemAlarmSettingStatusString1: 'alarm settings.system alarm.status 1',
    SystemAlarmSettingStatusString2: 'alarm settings.system alarm.status 2',
    SystemAlarmSettingStatusString3: 'alarm settings.system alarm.status 3',
    SystemAlarmSettingStatusString4: 'alarm settings.system alarm.status 4',
    SystemAlarmSettingStatusString5: 'alarm settings.system alarm.status 5',
    SystemAlarmSettingStatusString6: 'alarm settings.system alarm.status 6',
    SystemAlarmSettingStatusString7: 'alarm settings.system alarm.status 7',
    SystemAlarmSettingStatusString8: 'alarm settings.system alarm.status 8',
    SystemAlarmSettingStatusString9: 'alarm settings.system alarm.status 9',
    SystemAlarmSettingStatusString10: 'alarm settings.system alarm.status 10',
    SystemAlarmSettingStatusString11: 'alarm settings.system alarm.status 11',
    SystemAlarmSettingStatusString12: 'alarm settings.system alarm.status 12',
    SystemAlarmSettingStatusString13: 'alarm settings.system alarm.status 13',
    SystemAlarmSettingStatusString14: 'alarm settings.system alarm.status 14',
    SystemAlarmSettingStatusString15: 'alarm settings.system alarm.status 15',
    SystemAlarmSettingStatusString16: 'alarm settings.system alarm.status 16',
    SystemAlarmSettingAlarmGroupNo: 'alarm settings.system alarm.ex',
    SystemAlarmSettingReposeGroup1No: 'alarm settings.system alarm.r1',
    SystemAlarmSettingReposeGroup2No: 'alarm settings.system alarm.r2',
    SystemAlarmSettingDelayTimer: 'alarm settings.system alarm.delay (sec)',
    SystemAlarmSettingAlarmLevel: 'alarm settings.system alarm.alarm',

    StandardNameId: 'standard name id',
    Remarks: 'remarks',
};

const ErrorMessageProperties = {
    AlarmSettingHighSetPoint: 'H set point',
    AlarmSettingVeryHighSetPoint: 'HH set point',
    AlarmSettingLowSetPoint: 'L set point',
    AlarmSettingVeryLowSetPoint: 'LL set point',
};

const ExcelCellRowIndex = {};
Object.entries(ExcelParsedProperties).forEach(([key, value], index) => {
    ExcelCellRowIndex[ExcelParsedProperties[key]] = index + 1;
});

const ExcelKeysMap = new Map(Object.entries(ExcelParsedProperties));
const ExcelKeys = Array.from(ExcelKeysMap.keys()).map(key => key.charAt(0).toLowerCase() + key.slice(1));

const ExcelAllowedHeaders = new Set(Object.values(ExcelParsedProperties));

const LegacyExcelHeaders = {
    'ch.no(tag)': 'ch. no. (tag)',
};

const LegacyExcelHeaderSuffixes = [
    ['.alarm group no', '.ex'],
    ['.repose group1 no', '.r1'],
    ['.repose group2 no', '.r2'],
    ['.delay(sec)', '.delay (sec)'],
];

function normalizeLegacyExcelHeader(header: string): string {
    if (!header) return header;

    const legacyHeader = LegacyExcelHeaders[header as keyof typeof LegacyExcelHeaders];
    if (legacyHeader) return legacyHeader;

    for (const [legacySuffix, currentSuffix] of LegacyExcelHeaderSuffixes) {
        if (header.endsWith(legacySuffix)) {
            return header.slice(0, -legacySuffix.length) + currentSuffix;
        }
    }

    return header;
}

/** @type {ChannelOpsData} */
const ChannelOpsData = {
    id: 0,
    name: '',
    tag: '',
    vesselId: '',
};

const ExcelAlarmStringValues = {
    alarm: AlarmLevel.Alarm,
    warning: AlarmLevel.Warning,
    warn: AlarmLevel.Warning,
    caution: AlarmLevel.Caution,
};

const BlankChannelTypeStr = 'blank';

const ExcelChannelTypeMap = {
    'Digital (contact)': ChannelType.DIGITAL_CONTACT,
    'Digital (Motor)': ChannelType.DIGITAL_MOTOR,
    'Digital (Ext Comm.)': ChannelType.DIGITAL_EXT_COMM,
    'Digital (Logic)': ChannelType.DIGITAL_LOGIC,

    'Analog (4-20mA PT)': ChannelType.ANALOG_420MA_PT,
    'Analog (4-20mA AI)': ChannelType.ANALOG_420MA_AI,
    'Analog (1-5V)': ChannelType.ANALOG_1_5_V,
    'Analog (Temp Pt100 3-wire)': ChannelType.ANALOG_PT100_3WIRE,
    'Analog (Temp Pt100 2-wire)': ChannelType.ANALOG_PT100_2WIRE,
    'Analog (Temp K)': ChannelType.ANALOG_TEMP_K,
    'Analog (Ext Comm.)': ChannelType.ANALOG_EXT_COMM,
    'Analog (Logic)': ChannelType.ANALOG_LOGIC,

    'Exh gas dev': ChannelType.EXH_GAS_DEV,
    'Exh gas mean': ChannelType.EXH_GAS_MEAN,

    'Flow counter (Total)': ChannelType.FLOW_TOTAL,
    'Flow counter (Day)': ChannelType.FLOW_DAY,
    'Flow counter (Ext Comm.)': ChannelType.FLOW_EXT_COMM,
    'Flow counter (Logic)': ChannelType.FLOW_LOGIC,

    'Running hour (Total)': ChannelType.RUNNING_TOTAL,
    'Running hour (Day)': ChannelType.RUNNING_DAY,
    'Running hour (Logic)': ChannelType.RUNNING_LOGIC,

    Blank: ChannelType.BLANK,

    [systemChannelType]: ChannelType.SYSTEM,
};

const DigitalChannelTypes = new Set([ChannelType.DIGITAL_CONTACT, ChannelType.DIGITAL_MOTOR]);

const AnalogChannelTypes = new Set([
    ChannelType.ANALOG_420MA_PT,
    ChannelType.ANALOG_420MA_AI,
    ChannelType.ANALOG_1_5_V,
    ChannelType.ANALOG_PT100_3WIRE,
    ChannelType.ANALOG_PT100_2WIRE,
    ChannelType.ANALOG_TEMP_K,
    ChannelType.EXH_GAS_DEV,
    ChannelType.EXH_GAS_MEAN,
]);

const FlowChannelTypes = new Set([ChannelType.FLOW_TOTAL, ChannelType.FLOW_DAY]);

const exhGasDevChannelType = ChannelType.EXH_GAS_DEV;
const BlankChannelTypes = ChannelType.BLANK;
const MAX_GROUPS_COUNT = 101;

enum GroupsSheetColumns {
    rowId = 0,
    categoryExcelId = 1,
    categoryName = 2,

    rowNum = 4,
    groupExcelId = 5,
    groupName = 6,
    categoryRowId = 7,
}

const simpleExcelProps = {
    Id: 'id',
    Tag: 'ch. no. (tag)',
    Name: 'name',
    Status: 'status (normal/h/hh) status(l/ll) range',
    Unit: 'unit',
    setPoint_1: 'alm h alm l feedback',
    level_1: 'alm level',
    alarmGroup_1: 'ex',
    delay_1: 'dly',
    repose1_1: 'r1',
    repose2_1: 'r2',
    setPoint_2: 'alm hh alm ll',
    level_2: 'alm level',
    alarmGroup_2: 'ex',
    delay_2: 'dly',
    repose1_2: 'r1',
    repose2_2: 'r2',
    sensor: 'sen ex dly',
    sensor_repose1: 'r1',
    sensor_repose2: 'r2',
    channelType: 'in',
    special: 's',
    fcuAddress: 'fcu no. fu address',
    remarks: 'remarks',
};

const SimpleExcelKeys = Object.freeze({
    ID: 'ID',
    TAG: 'TAG',
    NAME: 'NAME',
    STATUS: 'STATUS',
    UNIT: 'UNIT',
    SET_POINT_1: 'SET_POINT_1',
    LEVEL_1: 'LEVEL_1',
    ALARM_GROUP_1: 'ALARM_GROUP_1',
    DELAY_1: 'DELAY_1',
    REPOSE1_1: 'REPOSE1_1',
    REPOSE2_1: 'REPOSE2_1',
    SET_POINT_2: 'SET_POINT_2',
    LEVEL_2: 'LEVEL_2',
    ALARM_GROUP_2: 'ALARM_GROUP_2',
    DELAY_2: 'DELAY_2',
    REPOSE1_2: 'REPOSE1_2',
    REPOSE2_2: 'REPOSE2_2',
    SENSOR: 'SENSOR',
    SENSOR_REPOSE1: 'SENSOR_REPOSE1',
    SENSOR_REPOSE2: 'SENSOR_REPOSE2',
    CHANNEL_TYPE: 'CHANNEL_TYPE',
    SPECIAL: 'SPECIAL',
    FCU_ADDRESS: 'FCU_ADDRESS',
    SlotNum: 'SlotNum',
    PinNum: 'PinNum',
    REMARKS: 'REMARKS',
    ROW_INDEX: 'ROW_INDEX',
});

const getGlobalColumns = (): string[] => [
    ExcelParsedProperties.Id,
    ExcelParsedProperties.FcuNo,
    ExcelParsedProperties.ChannelType,
    ExcelParsedProperties.Tag,
    ExcelParsedProperties.Name,
    ExcelParsedProperties.Status,
    ExcelParsedProperties.StandardNameId,
    ExcelParsedProperties.Remarks,
];

const getCommonAddressColumns = (): string[] => [
    ExcelParsedProperties.InputDataMemoryInputAddressAddress,
    ExcelParsedProperties.InputDataMemoryInputAddressMask,
    ExcelParsedProperties.OutputDataMemoryOutputAddressAddress,
    ExcelParsedProperties.OutputDataMemoryOutputAddressMask,
];

function getUpperLimitAlarms(): string[] {
    return [
        ExcelParsedProperties.AlarmSettingVeryHighEnable,
        ExcelParsedProperties.AlarmSettingVeryHighStatusString,
        ExcelParsedProperties.AlarmSettingVeryHighSetPoint,
        ExcelParsedProperties.AlarmSettingVeryHighAlarmGroupNo,
        ExcelParsedProperties.AlarmSettingVeryHighReposeGroup1No,
        ExcelParsedProperties.AlarmSettingVeryHighReposeGroup2No,
        ExcelParsedProperties.AlarmSettingVeryHighDelayTimer,
        ExcelParsedProperties.AlarmSettingVeryHighAlarmLevel,
        ExcelParsedProperties.AlarmSettingHighEnable,
        ExcelParsedProperties.AlarmSettingHighStatusString,
        ExcelParsedProperties.AlarmSettingHighSetPoint,
        ExcelParsedProperties.AlarmSettingHighAlarmGroupNo,
        ExcelParsedProperties.AlarmSettingHighReposeGroup1No,
        ExcelParsedProperties.AlarmSettingHighReposeGroup2No,
        ExcelParsedProperties.AlarmSettingHighDelayTimer,
        ExcelParsedProperties.AlarmSettingHighAlarmLevel,
    ];
}

function getLowerLimitAlarms(): string[] {
    return [
        ExcelParsedProperties.AlarmSettingLowEnable,
        ExcelParsedProperties.AlarmSettingLowStatusString,
        ExcelParsedProperties.AlarmSettingLowSetPoint,
        ExcelParsedProperties.AlarmSettingLowAlarmGroupNo,
        ExcelParsedProperties.AlarmSettingLowReposeGroup1No,
        ExcelParsedProperties.AlarmSettingLowReposeGroup2No,
        ExcelParsedProperties.AlarmSettingLowDelayTimer,
        ExcelParsedProperties.AlarmSettingLowAlarmLevel,
        ExcelParsedProperties.AlarmSettingVeryLowEnable,
        ExcelParsedProperties.AlarmSettingVeryLowStatusString,
        ExcelParsedProperties.AlarmSettingVeryLowSetPoint,
        ExcelParsedProperties.AlarmSettingVeryLowAlarmGroupNo,
        ExcelParsedProperties.AlarmSettingVeryLowReposeGroup1No,
        ExcelParsedProperties.AlarmSettingVeryLowReposeGroup2No,
        ExcelParsedProperties.AlarmSettingVeryLowDelayTimer,
        ExcelParsedProperties.AlarmSettingVeryLowAlarmLevel,
    ];
}

function getAnalogLimitAlarmColumns(): string[] {
    return [...getUpperLimitAlarms(), ...getLowerLimitAlarms()];
}

function getAnalogColumns(): string[] {
    return [
        ...getGlobalColumns(),
        ExcelParsedProperties.MeasurableRangeMin,
        ExcelParsedProperties.MeasurableRangeMax,
        ExcelParsedProperties.NormalRangeMin,
        ExcelParsedProperties.NormalRangeMax,
        ExcelParsedProperties.MeasurementUnit,
        ExcelParsedProperties.Scale,
        ExcelParsedProperties.FixedDigit,
        ExcelParsedProperties.InputDataFuInputAddressFuNo,
        ExcelParsedProperties.InputDataFuInputAddressSlotNo,
        ExcelParsedProperties.InputDataFuInputAddressPin,
        ExcelParsedProperties.InputDataMemoryInputAddressAddress,
        ExcelParsedProperties.InputDataMemoryInputAddressMask,
        ExcelParsedProperties.InputDataMiscHysteresis,
        ExcelParsedProperties.OutputDataMemoryOutputAddressAddress,
        ExcelParsedProperties.OutputDataMemoryOutputAddressMask,
        ...getAnalogLimitAlarmColumns(),
        ExcelParsedProperties.AlarmSettingSensorFailEnable,
        ExcelParsedProperties.AlarmSettingSensorFailStatusString,
        ExcelParsedProperties.AlarmSettingSensorFailAlarmGroupNo,
        ExcelParsedProperties.AlarmSettingSensorFailReposeGroup1No,
        ExcelParsedProperties.AlarmSettingSensorFailReposeGroup2No,
        ExcelParsedProperties.AlarmSettingSensorFailDelayTimer,
        ExcelParsedProperties.AlarmSettingSensorFailAlarmLevel,
    ];
}

function getAnalogSpecialColumns(): string[] {
    return [
        ...getGlobalColumns(),
        ExcelParsedProperties.MeasurableRangeMin,
        ExcelParsedProperties.MeasurableRangeMax,
        ExcelParsedProperties.NormalRangeMin,
        ExcelParsedProperties.NormalRangeMax,
        ExcelParsedProperties.MeasurementUnit,
        ExcelParsedProperties.Scale,
        ExcelParsedProperties.FixedDigit,
        ExcelParsedProperties.InputDataFuInputAddressFuNo,
        ExcelParsedProperties.InputDataFuInputAddressSlotNo,
        ExcelParsedProperties.InputDataFuInputAddressPin,
        ExcelParsedProperties.InputDataMemoryInputAddressAddress,
        ExcelParsedProperties.InputDataMemoryInputAddressMask,
        ExcelParsedProperties.InputDataMiscHysteresis,
        ExcelParsedProperties.OutputDataMemoryOutputAddressAddress,
        ExcelParsedProperties.OutputDataMemoryOutputAddressMask,
        ...getAnalogLimitAlarmColumns(),
    ];
}

function getDigitalColumns(): string[] {
    return [
        ...getGlobalColumns(),
        ExcelParsedProperties.InputDataFuInputAddressFuNo,
        ExcelParsedProperties.InputDataFuInputAddressSlotNo,
        ExcelParsedProperties.InputDataFuInputAddressPin,
        ExcelParsedProperties.InputDataMemoryInputAddressAddress,
        ExcelParsedProperties.InputDataMemoryInputAddressMask,
        ExcelParsedProperties.InputDataMiscDigitalInputFilter,
        ExcelParsedProperties.OutputDataMemoryOutputAddressAddress,
        ExcelParsedProperties.OutputDataMemoryOutputAddressMask,
        ExcelParsedProperties.DigitalAlarmSettingEnable,
        ExcelParsedProperties.DigitalAlarmSettingStatusStringContactOn,
        ExcelParsedProperties.DigitalAlarmSettingStatusStringContactOff,
        ExcelParsedProperties.DigitalAlarmSettingAlarmGroupNo,
        ExcelParsedProperties.DigitalAlarmSettingReposeGroup1No,
        ExcelParsedProperties.DigitalAlarmSettingReposeGroup2No,
        ExcelParsedProperties.DigitalAlarmSettingDelayTimer,
        ExcelParsedProperties.DigitalAlarmSettingNormalOpenFlag,
        ExcelParsedProperties.DigitalAlarmSettingAlarmLevel,
    ];
}

function getRunningHourColumns(): string[] {
    return [
        ...getGlobalColumns(),
        ExcelParsedProperties.MeasurableRangeMin,
        ExcelParsedProperties.MeasurableRangeMax,
        ExcelParsedProperties.MeasurementUnit,
        ExcelParsedProperties.FixedDigit,
        ExcelParsedProperties.InputDataFuInputAddressFuNo,
        ExcelParsedProperties.InputDataFuInputAddressSlotNo,
        ExcelParsedProperties.InputDataFuInputAddressPin,
        ...getCommonAddressColumns(),
        ...getUpperLimitAlarms(),
    ];
}

function getFlowCounterColumns(): string[] {
    return [
        ...getGlobalColumns(),
        ExcelParsedProperties.MeasurableRangeMin,
        ExcelParsedProperties.MeasurableRangeMax,
        ExcelParsedProperties.MeasurementUnit,
        ExcelParsedProperties.FixedDigit,
        ExcelParsedProperties.InputDataFuInputAddressFuNo,
        ExcelParsedProperties.InputDataFuInputAddressSlotNo,
        ExcelParsedProperties.InputDataFuInputAddressPin,
        ExcelParsedProperties.InputDataMemoryInputAddressAddress,
        ExcelParsedProperties.InputDataMemoryInputAddressMask,
        ExcelParsedProperties.InputDataMiscDigitalInputFilter,
        ExcelParsedProperties.OutputDataMemoryOutputAddressAddress,
        ExcelParsedProperties.OutputDataMemoryOutputAddressMask,
        ...getUpperLimitAlarms(),
    ];
}

const enabledDigitalColumns: string[] = [
    ExcelParsedProperties.DigitalAlarmSettingEnable,
    ExcelParsedProperties.DigitalAlarmSettingStatusStringContactOn,
    ExcelParsedProperties.DigitalAlarmSettingStatusStringContactOff,
    ExcelParsedProperties.DigitalAlarmSettingNormalOpenFlag,
];

const ChannelColumnRules: Record<string, string[]> = {
    [ChannelTypeStrings[ChannelType.DIGITAL_CONTACT]]: getDigitalColumns(),
    [ChannelTypeStrings[ChannelType.DIGITAL_EXT_COMM]]: getDigitalColumns(),
    [ChannelTypeStrings[ChannelType.DIGITAL_LOGIC]]: getDigitalColumns(),

    [ChannelTypeStrings[ChannelType.DIGITAL_MOTOR]]: [
        ...getGlobalColumns(),
        ExcelParsedProperties.InputDataFuInputAddressFuNo,
        ExcelParsedProperties.InputDataFuInputAddressSlotNo,
        ExcelParsedProperties.InputDataFuInputAddressPin,
        ExcelParsedProperties.InputDataFuInputAddressPinCount,
        ExcelParsedProperties.InputDataMemoryInputAddressAddress,
        ExcelParsedProperties.InputDataMemoryInputAddressMask,
        ExcelParsedProperties.InputDataMiscDigitalInputFilter,
        ExcelParsedProperties.OutputDataFuOutputAddressFuNo,
        ExcelParsedProperties.OutputDataFuOutputAddressSlotNo,
        ExcelParsedProperties.OutputDataFuOutputAddressPin,
        ExcelParsedProperties.OutputDataFuOutputAddressPinCount,
        ExcelParsedProperties.OutputDataMemoryOutputAddressAddress,
        ExcelParsedProperties.OutputDataMemoryOutputAddressMask,
        ExcelParsedProperties.OutputDataMiscSignalType,
        ExcelParsedProperties.OutputDataMiscSignalWidth,
        ExcelParsedProperties.MotorChannelSettingEnable,
        ExcelParsedProperties.MotorChannelSettingMotorType,
        ExcelParsedProperties.MotorChannelSettingStopStatusString,
        ExcelParsedProperties.MotorChannelSettingStbyStatusString,
        ExcelParsedProperties.MotorChannelSettingRunStatusString1,
        ExcelParsedProperties.MotorChannelSettingRunStatusString2,
        ExcelParsedProperties.MotorChannelSettingRunStatusString3,
        ExcelParsedProperties.MotorChannelSettingRunStatusString4,
        ExcelParsedProperties.MotorChannelSettingRunStatusString5,
        ExcelParsedProperties.MotorChannelSettingAlarmGroupNo,
        ExcelParsedProperties.MotorChannelSettingReposeGroup1No,
        ExcelParsedProperties.MotorChannelSettingReposeGroup2No,
        ExcelParsedProperties.MotorChannelSettingDelayTimer,
        ExcelParsedProperties.MotorChannelSettingAlarmLevel,
        ExcelParsedProperties.AlarmSettingFeedbackErrorEnable,
        ExcelParsedProperties.AlarmSettingFeedbackErrorStatusString,
        ExcelParsedProperties.AlarmSettingFeedbackErrorAlarmGroupNo,
        ExcelParsedProperties.AlarmSettingFeedbackErrorReposeGroup1No,
        ExcelParsedProperties.AlarmSettingFeedbackErrorReposeGroup2No,
        ExcelParsedProperties.AlarmSettingFeedbackErrorDelayTimer,
        ExcelParsedProperties.AlarmSettingFeedbackErrorAlarmLevel,
    ],

    [ChannelTypeStrings[ChannelType.ANALOG_420MA_PT]]: getAnalogColumns(),
    [ChannelTypeStrings[ChannelType.ANALOG_420MA_AI]]: getAnalogColumns(),
    [ChannelTypeStrings[ChannelType.ANALOG_1_5_V]]: getAnalogColumns(),
    [ChannelTypeStrings[ChannelType.ANALOG_PT100_3WIRE]]: getAnalogColumns(),
    [ChannelTypeStrings[ChannelType.ANALOG_PT100_2WIRE]]: getAnalogColumns(),
    [ChannelTypeStrings[ChannelType.ANALOG_TEMP_K]]: getAnalogColumns(),

    [ChannelTypeStrings[ChannelType.ANALOG_EXT_COMM]]: getAnalogSpecialColumns(),
    [ChannelTypeStrings[ChannelType.ANALOG_LOGIC]]: getAnalogSpecialColumns(),

    [ChannelTypeStrings[ChannelType.EXH_GAS_DEV]]: [
        ...getGlobalColumns(),
        ExcelParsedProperties.MeasurableRangeMin,
        ExcelParsedProperties.MeasurableRangeMax,
        ExcelParsedProperties.MeasurementUnit,
        ExcelParsedProperties.Scale,
        ExcelParsedProperties.FixedDigit,
        ExcelParsedProperties.InputDataMemoryInputAddressAddress,
        ExcelParsedProperties.InputDataMemoryInputAddressMask,
        ExcelParsedProperties.InputDataMiscHysteresis,
        ExcelParsedProperties.OutputDataMemoryOutputAddressAddress,
        ExcelParsedProperties.OutputDataMemoryOutputAddressMask,
        ...getAnalogLimitAlarmColumns(),
    ],

    [ChannelTypeStrings[ChannelType.EXH_GAS_MEAN]]: [
        ...getGlobalColumns(),
        ExcelParsedProperties.MeasurableRangeMin,
        ExcelParsedProperties.MeasurableRangeMax,
        ExcelParsedProperties.MeasurementUnit,
        ExcelParsedProperties.Scale,
        ExcelParsedProperties.FixedDigit,
        ...getCommonAddressColumns(),
        ExcelParsedProperties.AlarmSettingLowEnable,
        ExcelParsedProperties.AlarmSettingLowSetPoint,
    ],

    [ChannelTypeStrings[ChannelType.FLOW_TOTAL]]: getFlowCounterColumns(),
    [ChannelTypeStrings[ChannelType.FLOW_DAY]]: getFlowCounterColumns(),
    [ChannelTypeStrings[ChannelType.FLOW_EXT_COMM]]: getFlowCounterColumns(),
    [ChannelTypeStrings[ChannelType.FLOW_LOGIC]]: getFlowCounterColumns(),

    [ChannelTypeStrings[ChannelType.RUNNING_TOTAL]]: getRunningHourColumns(),
    [ChannelTypeStrings[ChannelType.RUNNING_DAY]]: getRunningHourColumns(),
    [ChannelTypeStrings[ChannelType.RUNNING_LOGIC]]: getRunningHourColumns(),

    [ChannelTypeStrings[ChannelType.SYSTEM]]: [
        ExcelParsedProperties.Id,
        ExcelParsedProperties.FcuNo,
        ExcelParsedProperties.ChannelType,
        ExcelParsedProperties.Tag,
        ExcelParsedProperties.Name,
        ExcelParsedProperties.Status,
        ExcelParsedProperties.SystemAlarmSettingEnable,
        ExcelParsedProperties.SystemAlarmSettingAlarmGroupNo,
        ExcelParsedProperties.SystemAlarmSettingReposeGroup1No,
        ExcelParsedProperties.SystemAlarmSettingReposeGroup2No,
        ExcelParsedProperties.SystemAlarmSettingDelayTimer,
        ExcelParsedProperties.SystemAlarmSettingAlarmLevel,
        ...Array.from({ length: 16 }, (_, i) => {
            const propKey = `SystemAlarmSettingStatusString${i + 1}` as keyof typeof ExcelParsedProperties;
            return ExcelParsedProperties[propKey];
        }),
        ExcelParsedProperties.StandardNameId,
        ExcelParsedProperties.Remarks,
    ],

    [ChannelTypeStrings[ChannelType.BLANK]]: [
        ExcelParsedProperties.Id,
        ExcelParsedProperties.ChannelType,
        ExcelParsedProperties.Remarks,
    ],
};

export {
    ExcelCellRowIndex,
    ExcelParsedProperties,
    ExcelGroupDisplayNameDataRowCount,
    ExcelAllowedHeaders,
    ExcelHeaderRowCount,
    ExcelSkipParsingSheets,
    BlankChannelTypeStr,
    BlankChannelTypes,
    ExcelChannelTypeMap,
    DigitalChannelTypes,
    FlowChannelTypes,
    AnalogChannelTypes,
    exhGasDevChannelType,
    ExcelAlarmStringValues,
    ChannelOpsData,
    ExcelKeys,
    ExcelKeysMap,
    ChannelRowKey,
    ExcelGroupCategory,
    GroupsSheetColumns,
    MAX_GROUPS_COUNT,
    simpleExcelProps,
    SimpleExcelKeys,
    ChannelColumnRules,
    ErrorMessageProperties,
    normalizeLegacyExcelHeader,
    enabledDigitalColumns,
};
